import { A, useNavigate } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import {
  IoArrowForward,
  IoCopyOutline,
  IoInformationCircleOutline,
} from "solid-icons/io";
import { VsLinkExternal } from "solid-icons/vs";
import {
  type Component,
  createMemo,
  For,
  type JSX,
  Match,
  Show,
  Switch,
} from "solid-js";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { DocumentCard } from "@/components/shared/documents/document-card";
import { SkillsTags } from "@/components/shared/tags";
import { UnauthenticatedApplyModal } from "@/components/shared/UnauthenticatedHourlyApplyModal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FixedSheet,
  FixedSheetContent,
  FixedSheetFooter,
  FixedSheetHeader,
  FixedSheetTrigger,
  ScrollableContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApplicationPezzotta } from "@/domains/application/api";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useGetCompanyV2 } from "@/domains/company/company/api";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { usePostHog } from "@/hooks/usePostHog";
import { formatDateAgo } from "@/libs/date";
import { formatJobCompensation } from "@/libs/format";
import { areAllValuesFalsy } from "@/libs/global";
import { createAdditionalJobOfferDocumentsQueryOptions } from "../api";
import { useJobListContext } from "../context/JobListContext";
import { useTrackJobOfferView } from "../hooks/use-track-job-offer-view";
import { useVisitedJobStore } from "../store/visited_job";
import type { JobOfferV2WithCompanyId } from "../types";
import { resolveApplyCtaAction } from "../utils/apply-cta";
import { buildExternalApplyUrl } from "../utils/apply-url";
import { getBounsArray } from "../utils/parser";
import {
  getJobOfferPublicBrand,
  isWorkspaceUnavailable,
} from "../utils/public-brand";
import { BonusItem } from "./bonus";
import { CompanyInfoScreenSwitch } from "./company-info/screen";
import {
  companyInfoScreen,
  companyQueryId,
  resolveCompanyInfoState,
} from "./company-info/state";
import { OfferSubcategoryChips } from "./offer-subcategory-chips";

const ContractDetails: Component<{
  label: string;
  value: string;
}> = (props) => {
  return (
    <div class="space-y-1">
      <p class="text-sm text-gray-600">{props.label}</p>
      <p class="text-sm font-medium">{props.value}</p>
    </div>
  );
};

const ContractDetailsWithBadge: Component<{
  label: string;
  value: string;
  badge: JSX.Element;
}> = (props) => {
  return (
    <div class="space-y-1">
      <p class="text-sm text-gray-600">{props.label}</p>
      <div class="flex items-center gap-2">
        <p class="text-sm font-medium">{props.value}</p>
        {props.badge}
      </div>
    </div>
  );
};

export const DetailContent: Component<{
  job: JobOfferV2WithCompanyId;
  isAlreadyApplied?: boolean;
  preview?: boolean;
  previewFiles?: File[];
}> = (props) => {
  const [authStore] = useAuthV2();
  const isAuthenticated = createMemo(() => !!authStore.profile?.id);
  // L'id, non `props.job`: l'effetto dentro l'hook deve rieseguirsi quando
  // cambia l'offerta, non a ogni nuovo oggetto con lo stesso id. In preview
  // non si conta nulla, e lo dice passando un id vuoto.
  useTrackJobOfferView(() => (props.preview ? "" : props.job.id));
  const brand = createMemo(() => getJobOfferPublicBrand(props.job));
  const isUnavailable = createMemo(
    () => props.job.is_active === false || isWorkspaceUnavailable(props.job),
  );
  /**
   * The id the company query is asked for — a memo, and passed to the query as
   * an accessor so that the two always read the same value. One source, two
   * readers.
   *
   * It was a plain const, on the reasoning that `useGetCompanyV2` captured its
   * argument at setup anyway, so the whole component held one company for its
   * whole life. That reasoning rested on `props.job` never changing under a
   * mounted instance, and that is not something this component controls: at the
   * two preview mount points nothing unmounts it between jobs, and even in the
   * joblist the guarantee is thinner than it looks — the sheet's closing
   * animation keeps the content mounted for 200ms after background clicks are
   * released, so an offer clicked inside that window reuses this instance and
   * would have shown the previous company's name over the new offer's details.
   * Making the id reactive removes the question instead of answering it.
   *
   * Which of the two empty cases produced it is decided by `companyQueryId`,
   * where it can be tested: the solo-freelancer branch disables the query but
   * renders the tooltip instead of this sheet, while an orphan offer — company
   * row gone, left join empty, `company_id` at `null` — mounts the sheet with a
   * query that can never run. That second one is FLOW EP4, and it is why the
   * resolver decides on the id before it looks at the query status: a disabled
   * query reads `pending` forever.
   */
  const companyId = createMemo(() =>
    companyQueryId(props.job, brand().isSoloFreelancer),
  );
  const companyQuery = useGetCompanyV2(companyId);
  const companyInfoState = createMemo(() =>
    resolveCompanyInfoState({ companyId: companyId(), query: companyQuery }),
  );
  const companyScreen = createMemo(() => companyInfoScreen(companyInfoState()));
  const readyCompany = createMemo(() => {
    const state = companyInfoState();
    return state.kind === "ready" ? state.company : null;
  });
  const { isJobVisited } = useVisitedJobStore();

  const { t } = useI18n();

  const hasNoBonuses = () => areAllValuesFalsy(props.job.bonus ?? {});
  const bonusArray = createMemo(() => {
    if (hasNoBonuses()) return [];
    return getBounsArray(props.job.bonus!);
  });

  const additionalDocumentsQuery = useQuery(() => ({
    ...createAdditionalJobOfferDocumentsQueryOptions(props.job.id),
    enabled: !!props.job.id && isAuthenticated() && !props.preview,
  }));
  // Create a map of filename to File for preview mode
  const previewFilesMap = createMemo(() => {
    if (!props.preview || !props.previewFiles) return new Map<string, File>();
    const map = new Map<string, File>();
    props.previewFiles.forEach((file) => {
      map.set(file.name, file);
    });
    return map;
  });

  // Handler to preview files in preview mode
  const handlePreviewFileView = (filename: string) => {
    const file = previewFilesMap().get(filename);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      // Note: Object URL will be cleaned up automatically when the page/tab is closed
      // or when the browser decides to free memory. No need to manually revoke.
    }
  };

  return (
    <div class="max-w-full mx-2 w-full lg:mx-auto border-collapse border-none">
      <div class="flex-1 w-full space-y-6">
        {/* Job Title and Company */}
        <div class="space-y-4 border-b pb-4 lg:pb-6">
          <div class="flex items-start gap-2 lg:gap-4">
            <div class="w-10 h-10 lg:w-12 lg:h-12 bg-gray-200 rounded-sm flex items-center justify-center shrink-0">
              <Show
                when={brand().imageUrl}
                fallback={
                  <span class="text-xl font-semibold text-gray-600">
                    {brand().fallback}
                  </span>
                }
              >
                <img
                  src={brand().imageUrl ?? ""}
                  alt={brand().name}
                  class="w-full h-full object-cover rounded-sm"
                />
              </Show>
            </div>
            <div class="flex-1">
              <h1 class="text-lg font-medium mb-1">{props.job.title}</h1>
              <Show
                when={!brand().isSoloFreelancer}
                fallback={
                  <Tooltip openDelay={100}>
                    <TooltipTrigger
                      as="span"
                      tabIndex={0}
                      class="inline-flex items-center gap-1 text-sm text-primary mt-1 cursor-help"
                    >
                      {brand().name}
                      <IoInformationCircleOutline
                        size={16}
                        class="shrink-0"
                        aria-hidden="true"
                      />
                    </TooltipTrigger>
                    <TooltipContent class="max-w-xs">
                      {t("joblist.detailSheet.soloFreelancerTooltip")}
                    </TooltipContent>
                  </Tooltip>
                }
              >
                <FixedSheet>
                  <FixedSheetTrigger class="text-sm text-primary hover:underline flex items-center gap-1 mt-1 cursor-pointer">
                    {brand().name}
                    <IoArrowForward size={16} class="ml-2" />
                  </FixedSheetTrigger>
                  <FixedSheetContent
                    side="right"
                    class="w-full md:max-w-2xl bg-background"
                  >
                    <FixedSheetHeader backType="back">
                      <SheetTitle class="text-sm font-medium">
                        {t("joblist.detailSheet.companyInfo")}
                      </SheetTitle>
                    </FixedSheetHeader>
                    <ScrollableContent class="bg-background">
                      {/*
                        Which screen this is was decided by `companyInfoScreen`
                        and the mapping to a component lives in its own file:
                        both halves are testable, and this file — which no test
                        mounts — is left with no decision of its own to get
                        wrong.
                      */}
                      <CompanyInfoScreenSwitch
                        screen={companyScreen()}
                        company={readyCompany()}
                        onRetry={() => companyQuery.refetch()}
                      />
                    </ScrollableContent>
                  </FixedSheetContent>
                </FixedSheet>
              </Show>
            </div>
          </div>

          <Show when={brand().label || isUnavailable()}>
            <div class="flex items-center gap-2">
              <Show when={brand().label}>
                <Badge variant="secondary">{brand().label}</Badge>
              </Show>
              <Show when={isUnavailable()}>
                <Badge variant="secondary">
                  {t("joblist.jobOffer.badge.closed")}
                </Badge>
              </Show>
            </div>
          </Show>

          <div class="flex items-center gap-2 flex-wrap">
            <Show when={props.job.payment_type}>
              {(pt) => (
                <Badge variant="info" size="sm" class="rounded-full">
                  {t(`joblist.jobOffer.paymentType.${pt()}`)}:{" "}
                  {formatJobCompensation(props.job)}
                </Badge>
              )}
            </Show>
            <Badge variant="light-primary" size="sm" class="rounded-full">
              {categoryLabel(props.job.job_category)}
            </Badge>
            <OfferSubcategoryChips
              category={props.job.job_category}
              keys={props.job.job_subcategory}
            />
          </div>

          {/* Skills Tags */}
          <Show when={props.job.skills && props.job.skills.length > 0}>
            <div class="flex items-center gap-2 flex-wrap">
              <SkillsTags
                skills={props.job.skills ?? []}
                slicedSkills={props.job.skills ?? []}
              />
            </div>
          </Show>
          <div class="flex items-center gap-2">
            <Show when={props.job.created_at}>
              {(createdAt) => (
                <p class="text-sm text-gray-500">
                  {t("joblist.jobOffer.badge.date", {
                    date: formatDateAgo(createdAt())!,
                  })}
                </p>
              )}
            </Show>
            <Switch>
              <Match when={props.isAlreadyApplied}>
                <p class="text-xs text-primary font-semibold">
                  {t("joblist.jobOffer.application.sent")}
                </p>
              </Match>
              <Match when={isJobVisited(props.job.id)}>
                <Badge variant="secondary" size="sm">
                  {t("joblist.jobOffer.application.alreadySeen")}
                </Badge>
              </Match>
            </Switch>
          </div>
        </div>

        {/* Position Description */}
        <div class="space-y-2 lg:space-y-4 border-b pb-4 lg:pb-6">
          <h2 class="text-base font-medium mb-2">
            {t("application.header.position_description")}
          </h2>
          <div
            class="prose prose-sm text-gray-700"
            innerHTML={props.job.description}
          />

          <Switch>
            <Match when={props.preview}>
              <div class="space-y-2">
                <p class="text-sm text-gray-700">Documenti aggiuntivi</p>
                <Show
                  when={props.job.assets && props.job.assets.length > 0}
                  fallback={
                    <p class="text-sm text-gray-700">
                      Nessun documento aggiuntivo
                    </p>
                  }
                >
                  <div class="space-y-2">
                    <For each={props.job.assets}>
                      {(asset) => (
                        <DocumentCard
                          asset={asset}
                          size="small"
                          onView={() => handlePreviewFileView(asset.filename)}
                        />
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </Match>
            <Match
              when={
                additionalDocumentsQuery.isSuccess &&
                additionalDocumentsQuery.data?.assets.length > 0
              }
            >
              <Show
                when={isAuthenticated()}
                fallback={
                  <Alert variant="default">
                    <AlertTitle class="text-sm font-medium">
                      Per vedere gli allegati, effettua l'accesso.
                    </AlertTitle>
                    <AlertDescription>
                      Le nostre offerte possono contenere allegati ad
                      integrazione della descrizione del lavoro.
                      <br />
                      Per visualizzarli, effettua{" "}
                      <A href="/auth" class="text-primary hover:underline">
                        l'accesso
                      </A>
                      .
                    </AlertDescription>
                  </Alert>
                }
              >
                <div class="space-y-2">
                  <p class="text-sm text-gray-700">Documenti aggiuntivi</p>
                  <div class="space-y-2">
                    <For each={additionalDocumentsQuery.data?.assets}>
                      {(asset) => <DocumentCard asset={asset} size="small" />}
                    </For>
                  </div>
                </div>
              </Show>
            </Match>
          </Switch>
        </div>

        {/* Offer Details */}
        <div class="space-y-2 lg:space-y-4 border-b pb-4 lg:pb-6">
          <h2 class="text-base font-medium mb-4">
            {t("application.header.contract_details")}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ContractDetails
              label={
                props.job.payment_type === "daily"
                  ? t("joblist.jobOffer.paymentType.daily")
                  : t("joblist.jobOffer.paymentType.fixed")
              }
              value={formatJobCompensation(props.job)}
            />
            <Show when={props.job.effort}>
              {(effort) => {
                const effortValue = effort();
                return (
                  <ContractDetails
                    label={t("joblist.jobOffer.application.effort")}
                    value={t(`joblist.jobOffer.effort.${effortValue}`)}
                  />
                );
              }}
            </Show>
            <Show when={props.job.experience_level}>
              {(experienceLevel) => {
                const level = experienceLevel();
                return (
                  <ContractDetails
                    label={t("jobOffers.create.detail.form.experienceLevel")}
                    value={t(`common.experience_level_options.${level}`)}
                  />
                );
              }}
            </Show>
            <Show when={props.job.project_type}>
              {(projectType) => (
                <ContractDetails
                  label={t("joblist.jobOffer.application.projectType")}
                  value={t(`joblist.jobOffer.projectType.${projectType()}`)}
                />
              )}
            </Show>
            <Show when={props.job.duration}>
              {(duration) => {
                const durationValue = duration();
                return (
                  <ContractDetailsWithBadge
                    label={t("joblist.jobOffer.application.duration")}
                    value={t(`joblist.jobOffer.duration.${durationValue}`)}
                    badge={
                      <Show when={props.job.renewable}>
                        <Badge variant="light-green" size="sm">
                          {t("common.renewable")}
                        </Badge>
                      </Show>
                    }
                  />
                );
              }}
            </Show>
            <Show when={props.job.start_date}>
              {(startDate) => {
                const startDateValue = startDate();
                return (
                  <ContractDetails
                    label={t("joblist.jobOffer.application.startDate")}
                    value={t(`common.start_date_options.${startDateValue}`)}
                  />
                );
              }}
            </Show>
            <Show when={props.job.estimated_deadline}>
              {(estimatedDeadline) => {
                const deadline = estimatedDeadline();
                return (
                  <ContractDetails
                    label={t("joblist.jobOffer.application.estimatedDeadline")}
                    value={new Date(deadline!).toLocaleDateString()}
                  />
                );
              }}
            </Show>
            <Show when={props.job.remote_policy}>
              {(remotePolicy) => {
                const policy = remotePolicy();
                return (
                  <ContractDetails
                    label={t("joblist.jobOffer.application.remotePolicy")}
                    value={t(`common.remote_policy_options.${policy}`)}
                  />
                );
              }}
            </Show>
            <Show when={props.job.location && props.job.location.length > 0}>
              <ContractDetails
                label={t("joblist.jobOffer.application.location")}
                value={props.job.location?.join(", ") ?? ""}
              />
            </Show>
          </div>
        </div>

        {/* Bonus Section */}
        <Show when={!hasNoBonuses()}>
          <div>
            <h2 class="text-base font-medium mb-2">
              {t("joblist.jobOffer.application.bonus")}
            </h2>
            <p class="text-sm text-gray-500 mb-3">
              {t("joblist.jobOffer.application.bonusDescription")}
            </p>
            <div class="space-y-3">
              <For each={bonusArray()}>
                {(bonus) => (
                  <BonusItem bonusKey={bonus.key} value={bonus.value} />
                )}
              </For>
            </div>
          </div>
        </Show>

        <div class="grid grid-cols-2 gap-3 pt-2">
          <div class="bg-blue-50 rounded-lg p-3">
            <p class="text-xs text-gray-600 mb-1">
              {t("joblist.detailSheet.view_count")}
            </p>
            <p class="text-base font-medium">{props.job.views_count ?? 0}</p>
          </div>
          <div class="bg-matisse-50 rounded-lg p-3">
            <p class="text-xs text-gray-600 mb-1">
              {t("joblist.detailSheet.application_count")}
            </p>
            <p class="text-base font-medium">
              {props.job.applications_count ?? 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DetailSheetActions: Component<{
  job: JobOfferV2WithCompanyId;
  isAlreadyApplied?: boolean;
}> = (props) => {
  const [authStore] = useAuthV2();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { capture } = usePostHog();
  const applicationPezzotta = useApplicationPezzotta();
  const isInactive = createMemo(
    () => props.job.is_active === false || isWorkspaceUnavailable(props.job),
  );
  const canApply = createMemo(
    () => !authStore.profile?.id || authStore.capabilities?.can_apply === true,
  );

  const handleApply = () => {
    capture("click_apply_button", {
      job_offer_id: props.job.id,
    });
    navigate(`/job-offers/${props.job.id}/apply`);
  };

  const handleApplyToExternalUrl = () => {
    capture("click_apply_button_to_external_url", {
      job_offer_id: props.job.id,
    });

    // `apply_url` è testo libero scritto dall'azienda: va interpretato come URL
    // prima di aprirlo, non concatenato. Vedi `buildExternalApplyUrl`.
    const destination = buildExternalApplyUrl(
      props.job.apply_url,
      props.job.id,
    );

    if (!destination) {
      // Un bottone che non fa niente è la classe di difetto che questo ramo
      // esiste per chiudere (I12): se non c'è dove mandare l'utente, glielo si
      // dice, e il caso resta contabile.
      capture("fail_external_apply_redirect", {
        job_offer_id: props.job.id,
      });
      toast.error(t("joblist.jobOffer.application.externalUrlUnavailable"));
      return;
    }

    // Aperta **sincrona al click**, non nel `.then()` della mutation (I12).
    // Due ragioni, entrambe osservate: il conteggio che fallisce non deve
    // lasciare il bottone senza effetto, e un `window.open` a promessa risolta
    // ha perso il gesto utente — i popup blocker lo fermano.
    window.open(destination, "_blank", "noopener,noreferrer");

    applicationPezzotta
      .mutateAsync({
        job_offer_id: props.job.id,
      })
      .then(() => {
        capture("create_job_application", {
          job_offer_id: props.job.id,
        });
      })
      .catch((error) => {
        // All'utente non si dice niente: il conteggio è nostro, la candidatura
        // è già partita altrove, e un errore qui sarebbe rumore su un'azione
        // riuscita. Ma **muto non vuol dire invisibile**: un guasto sistematico
        // dell'endpoint spegnerebbe `create_job_application` senza che nessuno
        // sappia perché, e quell'evento è la misura con cui questo ramo è stato
        // giudicato riparato (FLOW §metriche).
        console.error("Failed to record external job application", error);
        capture("fail_job_application_count", {
          job_offer_id: props.job.id,
        });
      });
  };

  const handleCopy = (e: MouseEvent) => {
    e.stopPropagation();
    capture("send_job_offer_link", {
      job_offer_id: props.job.id,
    });
    const shareUrl = `${window.location.origin}/job-offers/${props.job.id}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Copied to clipboard");
  };

  /**
   * L'ordine dei rami non vive più qui: `<Match>` legge un valore, non una
   * posizione. Prima l'ordine *era* la logica, e sbagliarlo si vedeva solo in
   * runtime da anonimo (I12).
   */
  const ctaAction = createMemo(() =>
    resolveApplyCtaAction({
      isClosed: isInactive(),
      isAuthenticated: !!authStore.profile?.id,
      hasExternalUrl: !!props.job.apply_url,
      isAlreadyApplied: !!props.isAlreadyApplied,
      canApply: canApply(),
    }),
  );

  return (
    <div class="space-y-3 w-full">
      <Switch
        // Rete di sicurezza: i sei rami coprono le sei azioni di oggi, ma niente
        // lega le due liste a compile time. Una settima azione aggiunta a
        // `ApplyCtaAction` senza il suo `<Match>` qui compilerebbe e non
        // renderizzerebbe **niente** — di nuovo un bottone che non esiste, la
        // stessa classe di difetto (I12) che `resolveApplyCtaAction` è nato per
        // chiudere. Un bottone disabilitato e onesto è il peggio accettabile.
        fallback={
          <Button size="lg" class="w-full" variant="default" disabled>
            {t("joblist.jobOffer.application.applyUnavailable")}
          </Button>
        }
      >
        <Match when={ctaAction() === "closed"}>
          <Button size="lg" class="w-full" variant="default" disabled>
            {t("joblist.jobOffer.badge.closed")}
          </Button>
        </Match>
        <Match when={ctaAction() === "login"}>
          <UnauthenticatedApplyModal
            jobId={props.job.id}
            jobTitle={props.job.title}
          >
            <Button size="lg" class="w-full" variant="default">
              {t("joblist.jobOffer.application.applyNow")}
            </Button>
          </UnauthenticatedApplyModal>
        </Match>
        <Match when={ctaAction() === "external"}>
          <Button
            size="lg"
            class="w-full"
            variant="default"
            onClick={handleApplyToExternalUrl}
          >
            {t("joblist.jobOffer.application.applyNow")}
            <VsLinkExternal size={16} class="ml-2" />
          </Button>
        </Match>
        <Match when={ctaAction() === "already_applied"}>
          <Button size="lg" class="w-full" variant="default" disabled>
            {t("joblist.jobOffer.application.alreadyApplied")}
          </Button>
        </Match>
        <Match when={ctaAction() === "unavailable"}>
          <Button size="lg" class="w-full" variant="default" disabled>
            {t("joblist.jobOffer.application.applyUnavailable")}
          </Button>
        </Match>
        <Match when={ctaAction() === "apply"}>
          <Button size="lg" class="w-full" onClick={handleApply}>
            {t("joblist.jobOffer.application.applyNow")}
          </Button>
        </Match>
      </Switch>
      <Button variant="outline" size="lg" class="w-full" onClick={handleCopy}>
        <span class="text-xs text-gray-500">Share this offer</span>
        <IoCopyOutline size={16} class="ml-2" />
      </Button>
      <Show
        when={props.job.apply_url}
        fallback={
          <p class="text-xs text-gray-500 mt-2">
            {t("joblist.jobOffer.application.scam_warning")}
          </p>
        }
      >
        <p class="text-xs text-gray-500 mt-2">
          {t("joblist.jobOffer.application.external_offer")}
        </p>
      </Show>
    </div>
  );
};

export const DetailSheet: Component = () => {
  const { selectedJob, isSheetOpen, setIsSheetOpen } = useJobListContext();
  const { t } = useI18n();
  return (
    <FixedSheet open={isSheetOpen()} onOpenChange={setIsSheetOpen}>
      <FixedSheetContent side="right" class="w-full md:max-w-2xl bg-card">
        <FixedSheetHeader>
          <SheetTitle class="text-sm font-medium ">
            <div class="flex items-center gap-2 w-full justify-between pr-4">
              <span class="text-sm font-medium">
                {t("joblist.detailSheet.title")}
              </span>
              <A
                href={`/job-offers/${selectedJob()?.job.id}`}
                class="text-sm text-gray-500 flex items-center hover:underline cursor-pointer"
                target="_blank"
              >
                {t("common.open_in_new_tab")}
                <VsLinkExternal size={16} class="ml-2" />
              </A>
            </div>
          </SheetTitle>
        </FixedSheetHeader>
        <ScrollableContent>
          <Show when={selectedJob()}>
            {(job) => (
              <DetailContent
                job={job().job}
                isAlreadyApplied={job().isAlreadyApplied}
              />
            )}
          </Show>
        </ScrollableContent>
        <FixedSheetFooter>
          <Show when={selectedJob()}>
            {(job) => (
              <DetailSheetActions
                job={job().job}
                isAlreadyApplied={job().isAlreadyApplied}
              />
            )}
          </Show>
        </FixedSheetFooter>
      </FixedSheetContent>
    </FixedSheet>
  );
};
